import React from 'react';
import { Tag, Tooltip, Typography, theme } from 'antd';
import { CloudServerOutlined, MobileOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { CHIP_STYLE, HealthServer, HistoryEntry, HISTORY_BARS, SPARKLINE_COLORS } from './healthMonitorTypes';

const { Text } = Typography;

// Row cells for the Health Monitor table. Every one of them is a single line
// of primary content — the secondary facts (packet loss, visibility, country
// name, full tag list) live in the tooltip, not in another column.

const ELLIPSIS: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text type="secondary" style={{ fontSize: 12 }}>{children}</Text>
);

// The history list arrives newest-first and can hold 100 entries; drawing all
// of them made a 600px wide SVG inside a 150px column. The recent window is
// what the screen is for, so only the newest bars are drawn.

export const Sparkline: React.FC<{ history: HistoryEntry[] }> = ({ history }) => {
  if (!history || history.length === 0) {
    // Same 14px box as a drawn strip, so the two rows of a History cell stay
    // aligned when only one of them has data yet.
    return <span style={{ lineHeight: '14px', fontSize: 12, opacity: 0.45 }}>--</span>;
  }
  const items = history.slice(0, HISTORY_BARS).reverse();
  const barW = 4;
  const gap = 1;
  const h = 14;
  const w = items.length * (barW + gap);
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {items.map((entry, i) => (
        <rect
          key={i}
          x={i * (barW + gap)}
          y={0}
          width={barW}
          height={h}
          fill={SPARKLINE_COLORS[entry.status] || SPARKLINE_COLORS.UNKNOWN}
          rx={1}
        />
      ))}
    </svg>
  );
};

// The row label used to be a bare "N" / "P" / "M" with no legend anywhere on
// the screen, so it is the word itself. Label and strip form one fixed-width
// unit: the label is right-aligned against its strip and single-line, so the
// two rows read as two labelled bars rather than a "Net / Proxy" text block,
// and the strips line up down the whole column.
const LABEL_WIDTH = 40;
const ROW_H = 14;

const SparkRow: React.FC<{ label: string; title: string; history: HistoryEntry[] }> = ({ label, title, history }) => (
  <Tooltip title={`${title} — oldest to newest, last ${HISTORY_BARS} checks`}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: ROW_H, cursor: 'help' }}>
      <Text
        type="secondary"
        style={{
          fontSize: 12,
          lineHeight: `${ROW_H}px`,
          width: LABEL_WIDTH,
          flex: '0 0 auto',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <Sparkline history={history} />
    </div>
  </Tooltip>
);

export const HistoryCell: React.FC<{ record: HealthServer }> = ({ record }) => {
  const isMobile = record.type === 'mobile-gateway';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {!isMobile && <SparkRow label="Net" title="Network history" history={record.networkHistory} />}
      <SparkRow
        label={isMobile ? 'Modem' : 'Proxy'}
        title={isMobile ? 'Modem history' : 'Proxy history'}
        history={record.proxyHistory}
      />
    </div>
  );
};

// The column header carries the legend the bars need: which way time runs and
// what the colours mean. It is the one place a legend can live without costing
// a row of vertical space on a 728px-tall screen.
export const HistoryHeader: React.FC = () => (
  <Tooltip
    title={
      <div style={{ fontSize: 12 }}>
        <div>Last {HISTORY_BARS} checks, oldest on the left.</div>
        <div style={{ marginTop: 4 }}>
          <b>Net</b> — this server's network IPs.<br />
          <b>Proxy</b> / <b>Modem</b> — its proxy endpoints.
        </div>
        <div style={{ marginTop: 4 }}>Bar colour is the state of that check.</div>
      </div>
    }
  >
    <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>History</span>
  </Tooltip>
);

// Server status dot. It leads the row in a fixed 8px slot so the column of
// dots is the first thing the eye meets and lines up down all 250 rows —
// wedged in front of the IP it sat at a different x on every row and competed
// with the type icon for "first glyph".
export const StatusDot: React.FC<{ record: HealthServer }> = ({ record }) => {
  const status = record.status || 'UNKNOWN';
  const color = SPARKLINE_COLORS[status] || SPARKLINE_COLORS.UNKNOWN;
  return (
    <Tooltip title={`Server status: ${status}`}>
      <span
        role="img"
        aria-label={`Status ${status}`}
        style={{ flex: '0 0 auto', width: 8, height: 8, borderRadius: '50%', backgroundColor: color, cursor: 'help' }}
      />
    </Tooltip>
  );
};

// Status dot, then the name (the primary fact, body weight), then country and
// type as quiet trailing marks. Flag and type icon used to come first and the
// mobile icon was painted in the primary colour — colour this screen reserves
// for status. The name is truncated with an ellipsis rather than wrapped.
export const NameCell: React.FC<{ record: HealthServer }> = ({ record }) => {
  const { token } = theme.useToken();
  const isMobile = record.type === 'mobile-gateway';
  const typeLabel = isMobile ? 'Mobile gateway' : 'Dedicated server';
  const code = record.country;
  const TypeIcon = isMobile ? MobileOutlined : CloudServerOutlined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <StatusDot record={record} />
      <Tooltip title={`${record.name} — ${typeLabel}${code ? ` — ${code}` : ''}`}>
        <Link to={`/servers/${record.key}/health`} style={ELLIPSIS}>{record.name}</Link>
      </Tooltip>
      <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        <Tooltip title={typeLabel}>
          <TypeIcon aria-label={typeLabel} style={{ color: token.colorTextTertiary }} />
        </Tooltip>
        <Tooltip title={code ? `Country: ${code}` : 'Country not detected yet'}>
          {code ? (
            <img
              src={`https://flagcdn.com/20x15/${code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/40x30/${code.toLowerCase()}.png 2x`}
              width={16}
              height={12}
              alt={code}
              style={{ display: 'block' }}
              onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
            />
          ) : (
            <span style={{ width: 16, height: 12, display: 'block' }} />
          )}
        </Tooltip>
      </span>
    </div>
  );
};

// Address + ping on one line; the status dot now leads the row (NameCell).
// The tooltip keeps status, loss and visibility.
export const MainIPCell: React.FC<{ record: HealthServer }> = ({ record }) => {
  const status = record.status || 'UNKNOWN';
  const { pingLatency: ping, packetLoss: pl, visibility: vis, visibilityChecked: visChecked } = record;
  const details = [
    ping != null ? `${ping.toFixed(0)}ms` : null,
    pl != null ? `PL ${pl.toFixed(0)}%` : null,
    vis != null && visChecked ? `Vis ${vis.toFixed(0)}%` : vis != null && !visChecked ? 'Vis --' : null,
  ].filter(Boolean).join(' · ');
  const prefix = record.type === 'mobile-gateway' ? 'Gateway: ' : '';
  return (
    <Tooltip title={`${prefix}${status}${details ? ' — ' + details : ''}`}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, cursor: 'help' }}>
        <span style={{ ...ELLIPSIS, fontSize: 13 }}>{record.mainIP}</span>
        {/* A DOWN server reports ping 0, and "0ms" next to a red dot reads as
            "instant", the exact opposite of the truth. Sub-millisecond is not
            worth a column inch either; the tooltip still carries the number. */}
        {ping != null && ping >= 1 && (
          <span style={{ flex: '0 0 auto' }}><Muted>{ping.toFixed(0)}ms</Muted></span>
        )}
      </span>
    </Tooltip>
  );
};

export const TagsCell: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (!tags || tags.length === 0) return <Muted>--</Muted>;
  const [first, ...rest] = tags;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
      <Tooltip title={tags.join(', ')}>
        <Tag style={CHIP_STYLE}>{first}</Tag>
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip title={rest.join(', ')}>
          <Tag style={{ ...CHIP_STYLE, flex: '0 0 auto' }}>+{rest.length}</Tag>
        </Tooltip>
      )}
    </div>
  );
};
